(ns shared.core.amelia-light.components.input
  (:require [clojure.string :as s]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.components.input :as input]
            ["@up/base-ui/components/ui/input" :refer [Input]]
            ["@up/base-ui/components/ui/textarea" :refer [Textarea]]
            ["@up/base-ui/components/ui/password-input" :refer [PasswordInput]]
            ["@up/base-ui/components/ui/label" :refer [Label]]
            ["@up/base-ui/components/ui/input-group" :refer [InputGroup
                                                             InputGroupAddon
                                                             InputGroupButton
                                                             InputGroupText
                                                             InputGroupInput
                                                             InputGroupTextarea
                                                             InputGroupClearButton]]))

(def input-class-name "amelia-light-input")
(def input-container-class-name "amelia-light-input-container")
(def textarea-class-name "amelia-light-textarea")

(defn input-class-name-with [with-str]
  (str "." input-class-name ".input" with-str))

(defn textarea-class-name-with [with-str]
  (str "." textarea-class-name ".textarea" with-str))

(style/add-css [(input-class-name-with " input") {:background-color "#fff"
                                                  :border "1px solid rgba(0, 0, 0, 0.2)"
                                                  :color "#000"
                                                  :font-family style/roboto-font-family
                                                  :font-size "12px"
                                                  :padding "6px"}]
               [(input-class-name-with " input:focus") {:box-shadow "0 2px 3px 0 rgba(34,36,38,.15)"
                                                        :border "1px solid rgba(0, 0, 0, 0.2)"
                                                        :color "#000"}]
               [(input-class-name-with ".pre-contents input") {:padding "6px 6px 6px 22px"}]

               [(textarea-class-name-with "") {:background-color "#fff"
                                               :border "1px solid rgba(0, 0, 0, 0.2)"
                                               :color "#000"
                                               :font-family style/roboto-font-family
                                               :font-size "12px"
                                               :padding "6px"}]
               [(textarea-class-name-with ":focus") {:box-shadow "0 2px 3px 0 rgba(34,36,38,.15)"
                                                     :border "1px solid rgba(0, 0, 0, 0.2)"
                                                     :color "#000"}])

(defn input [{:keys [class-name] :as args}]
  [input/input (merge args
                      {:container-class-name input-container-class-name
                       :class-name (str input-class-name
                                        (when class-name (str " " class-name)))
                       :clearable-icon-style {:font-size 12}})])

(defn textarea [{:keys [class-name] :as args}]
  [input/textarea (merge args
                         {:class-name (str textarea-class-name
                                           (when class-name (str " " class-name)))})])

(defn- with-focus-handlers [{:keys [on-focus on-blur] :as args}]
  (assoc args
    :on-focus #(do
                 (re/dispatch [:keybind/pause-current-route-bindings])
                 (when on-focus (on-focus %)))
    :on-blur #(do
                (re/dispatch [:keybind/resume-current-route-bindings])
                (when on-blur (on-blur %)))))

(defn- passes-regex? [regex v]
  (or (not regex) (s/blank? v) (re-matches regex v)))

(defn- raw-input-cmp [cmp-type {:keys [on-change regex] :as args}]
  (let [on-change (when on-change
                    (fn [e]
                      (when (passes-regex? regex (.. e -target -value))
                        (on-change e))))]
    [:> cmp-type (cond-> (dissoc (with-focus-handlers args) :form-path :form-name :regex)
                         on-change (assoc :on-change on-change))]))

;; Backing an input with a form (form-path + form-name) makes it reflect external
;; changes to the form value (resets/clears/programmatic sets) without controlling
;; :value — which on these foreign components would jump the caret to the end on
;; every keystroke. The element stays uncontrolled while typing; when the form
;; value changes to something the user didn't just type, we sync the DOM to it.
;; If the element is focused we set .value directly (remounting would reset the
;; caret); otherwise we bump :key to remount it. Form writes use dispatch-sync so
;; a re-render between a keystroke and its event can't see a stale db value and
;; mistake it for an external change.
(defn- form-input-cmp [cmp-type {:keys [form-path form-name]}]
  (let [val- (re/subscribe [:form/val- form-path form-name])
        version* (atom 0)
        last-val* (atom ::init)
        node* (atom nil)]
    (fn [cmp-type {:keys [form-path form-name on-change on-focus regex] :as args}]
      (let [db-val @val-
            first? (= @last-val* ::init)
            node @node*
            focused? (and node (= node (.-activeElement js/document)))]
        (cond
          first? (reset! last-val* db-val)
          (not= db-val @last-val*) (do (reset! last-val* db-val)
                                       (if focused?
                                         (set! (.-value node) (or db-val ""))
                                         (swap! version* inc))))
        [:> cmp-type
         (-> (with-focus-handlers (assoc args
                                    :on-focus (fn [e]
                                                (reset! node* (.-target e))
                                                (when on-focus (on-focus e)))))
             (dissoc :form-path :form-name :regex)
             (assoc :key @version*
                    :default-value (if (and first? (contains? args :default-value))
                                     (:default-value args)
                                     (or db-val ""))
                    :on-change (fn [e]
                                 (let [v (.. e -target -value)]
                                   (when (passes-regex? regex v)
                                     (reset! last-val* v)
                                     (if on-change
                                       (on-change e)
                                       (re/dispatch-sync [:form/change form-path form-name v])))))))]))))

(defn up-input-cmp [cmp-type {:keys [form-path form-name] :as args}]
  (if (and form-path form-name)
    [form-input-cmp cmp-type args]
    [raw-input-cmp cmp-type args]))

(defn up-input [args]
  [up-input-cmp Input args])

(defn up-textarea [args]
  [up-input-cmp Textarea args])

(defn up-password-input [args] [:> PasswordInput args])
(defn up-label [& args] (into [:> Label] args))
(defn up-input-group [& args] (into [:> InputGroup] args))
(defn up-input-group-addon [& args] (into [:> InputGroupAddon] args))
(defn up-input-group-button [& args] (into [:> InputGroupButton] args))
(defn up-input-group-text [& args] (into [:> InputGroupText] args))
(defn up-input-group-input [& args] (into [:> InputGroupInput] args))
(defn up-input-group-textarea [& args] (into [:> InputGroupTextarea] args))
(defn up-input-group-clear-button [& args] (into [:> InputGroupClearButton] args))
